import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBox,
  FaChevronRight,
  FaClock,
  FaExclamationTriangle,
  FaTruck,
  FaWrench,
} from 'react-icons/fa';
import { useAuth } from '../auth/AuthContext';
import { useVehicle } from '../contexts/VehicleContext';
import { homeFeedService } from '../services/apiService';

const FUEL_EMOJI = {
  Petrol: '⛽',
  Diesel: '🛢️',
  Electric: '⚡',
  CNG: '🔥',
  Hybrid: '🔋',
};

const ACTIVE_ORDER_STATES = new Set(['PENDING', 'DISPATCHED']);

function formatVehiclePill(vehicle) {
  if (!vehicle) return null;
  const model = vehicle.model || vehicle.vehicleModel || '';
  const variant = vehicle.variant || vehicle.vehicleVariant || '';
  const year = vehicle.year || vehicle.vehicleYear || '';
  const fuel = vehicle.fuelType || 'Petrol';
  const emoji = FUEL_EMOJI[fuel] || '🚗';
  const label = [model, variant, year].filter(Boolean).join(' ').trim() || vehicle.make || 'My vehicle';
  return `${label} ${emoji}`;
}

function stateIcon(state) {
  if (state === 'DISPATCHED') return <FaTruck className="text-blue-600" />;
  return <FaBox className="text-amber-600" />;
}

function isActiveOrder(order) {
  return order && ACTIVE_ORDER_STATES.has(order.state);
}

function isExpiringWarranty(warranty) {
  return warranty && warranty.daysLeft >= 0 && warranty.daysLeft <= 7;
}

const HomeFeedSection = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { selectedVehicle, vehicles } = useVehicle();

  const [feed, setFeed] = useState(null);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);

  const activeVehicle = useMemo(() => {
    if (selectedVehicle) return selectedVehicle;
    return vehicles.find((v) => v.isDefault) || vehicles[0] || null;
  }, [selectedVehicle, vehicles]);

  const loadFeed = useCallback(async () => {
    try {
      setLoading(true);
      const trendingPromise = homeFeedService.getTrending().catch(() => []);
      const feedPromise = isAuthenticated
        ? homeFeedService.getHomeFeed().catch(() => ({
            activeOrder: null,
            expiringWarranties: [],
            overdueReminders: [],
            hasPlacedOrder: false,
          }))
        : Promise.resolve(null);

      const [feedData, trendingTiles] = await Promise.all([feedPromise, trendingPromise]);
      setFeed(feedData);
      setTrending(Array.isArray(trendingTiles) ? trendingTiles.slice(0, 8) : []);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  const handleTrendingTap = (tile) => {
    const slug = tile.categorySlug || tile.slug || '';
    const p = new URLSearchParams();
    if (slug) p.set('category', slug);
    p.set('q', tile.title || slug || 'parts');
    const vid = activeVehicle?._id || activeVehicle?.id;
    if (vid) p.set('vehicleId', vid);
    navigate(`/catalog?${p.toString()}`);
  };

  const vehiclePillLabel = formatVehiclePill(activeVehicle);
  const activeOrder = isActiveOrder(feed?.activeOrder) ? feed.activeOrder : null;
  const expiringWarranties = (feed?.expiringWarranties || []).filter(isExpiringWarranty);
  const firstWarranty = expiringWarranties[0];
  const firstReminder = feed?.overdueReminders?.[0];

  return (
    <section className="mx-auto w-full max-w-6xl px-4 py-4 sm:px-6 sm:py-6">
      <button
        type="button"
        onClick={() => navigate('/vehicle-picker')}
        className="inline-flex max-w-full items-center gap-1 rounded-full border border-slate-200/80 bg-white/90 px-4 py-2 text-sm font-medium text-slate-800 shadow-sm hover:border-blue-300 dark:border-slate-600 dark:bg-slate-800/90 dark:text-slate-100"
      >
        {vehiclePillLabel || 'Add your car'}
        <FaChevronRight className="text-xs text-slate-400" />
      </button>

      {loading ? (
        <p className="mt-6 text-center text-sm text-slate-500">Loading your feed…</p>
      ) : (
        <div className="mt-5 space-y-4">
          {activeOrder && (
            <button
              type="button"
              onClick={() =>
                navigate(
                  activeOrder.orderId
                    ? `/myorder?orderId=${encodeURIComponent(activeOrder.orderId)}`
                    : '/myorder'
                )
              }
              className="flex w-full items-center gap-3 rounded-xl border border-blue-100 bg-white p-4 text-left shadow-sm hover:border-blue-200 dark:border-blue-900/50 dark:bg-slate-800/90"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-950">
                {stateIcon(activeOrder.state)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-slate-900 dark:text-slate-100">
                  {activeOrder.partName}
                </p>
                <p className="text-xs text-slate-500">
                  {activeOrder.state}
                  {activeOrder.eta && (
                    <>
                      {' '}
                      · ETA{' '}
                      {new Date(activeOrder.eta).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                      })}
                    </>
                  )}
                </p>
              </div>
              <FaChevronRight className="shrink-0 text-slate-300" />
            </button>
          )}

          {firstWarranty && (
            <button
              type="button"
              onClick={() => navigate('/warranty-cards')}
              className="flex w-full items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-left shadow-sm hover:bg-amber-100/80"
            >
              <FaExclamationTriangle className="mt-0.5 shrink-0 text-amber-600" />
              <div>
                <p className="font-medium text-amber-950">
                  {firstWarranty.partName} warranty expires in {firstWarranty.daysLeft} day
                  {firstWarranty.daysLeft === 1 ? '' : 's'}.
                </p>
                {expiringWarranties.length > 1 && (
                  <p className="mt-1 text-xs text-amber-800">
                    +{expiringWarranties.length - 1} more expiring soon
                  </p>
                )}
              </div>
            </button>
          )}

          {firstReminder && (
            <button
              type="button"
              onClick={() => navigate('/garages')}
              className="flex w-full items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm hover:border-emerald-200 dark:border-slate-600 dark:bg-slate-800/90"
            >
              <FaWrench className="mt-0.5 shrink-0 text-emerald-600" />
              <p className="text-sm text-slate-800 dark:text-slate-200">
                Your {firstReminder.vehicleLabel || 'vehicle'} is{' '}
                <strong>{firstReminder.overdueKm.toLocaleString('en-IN')} km</strong> overdue for an{' '}
                {firstReminder.serviceType}.
              </p>
            </button>
          )}

          <div>
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Trending
            </h2>
            <div className="grid grid-cols-4 gap-2 sm:gap-3">
              {trending.map((tile) => (
                <button
                  key={tile.id || tile.slug}
                  type="button"
                  onClick={() => handleTrendingTap(tile)}
                  className="flex flex-col items-center rounded-xl border border-slate-200 bg-white p-2 shadow-sm transition hover:border-blue-300 hover:shadow-md dark:border-slate-600 dark:bg-slate-800/90 sm:p-3"
                >
                  {tile.imageUrl ? (
                    <img
                      src={tile.imageUrl}
                      alt=""
                      className="mb-1.5 h-10 w-10 object-contain"
                    />
                  ) : (
                    <div className="mb-1.5 flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-xs font-bold text-slate-600 dark:bg-slate-700 dark:text-slate-200">
                      {String(tile.title || '?').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <span className="line-clamp-2 text-center text-[10px] font-medium leading-tight text-slate-700 dark:text-slate-200 sm:text-xs">
                    {tile.title}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {isAuthenticated && !feed?.hasPlacedOrder && (
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-center text-xs text-slate-600 dark:border-slate-600 dark:bg-slate-800/90 dark:text-slate-300 sm:text-sm">
              <FaClock className="mr-1 inline text-slate-400" />
              10-day returns | Verified sellers | Platform fees transparent on checkout
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default HomeFeedSection;
